import { TB_UPLOAD_MESSAGES, TB_UPLOAD_STAGE } from './constants';

/** Idle tbUpload. A new object each call so Vuex state is not shared. */
export function emptyTbUpload() {
  return {
    inProgress: false,
    queueId: null,
    stage: null,
    startedBy: null,
    startedAt: null,
    totalRows: 0,
    processedRows: 0,
    lastResult: null,
  };
}

function person(raw) {
  if (!raw || typeof raw !== 'object') return null;
  return {
    _id: raw._id == null ? '' : String(raw._id),
    name: raw.name == null ? '' : String(raw.name),
  };
}

function lastResult(raw) {
  if (!raw || typeof raw !== 'object') return null;
  return {
    queueId: raw.queueId == null ? null : raw.queueId,
    stage: raw.stage == null ? null : Number(raw.stage),
    finishedAt: raw.finishedAt || null,
    error: raw.error || null,
    startedBy: person(raw.startedBy),
  };
}

/** Coerce the shared status object. Missing input is idle, not an error. */
export function normalizeTbUpload(raw) {
  if (!raw || typeof raw !== 'object') return emptyTbUpload();
  return {
    inProgress: !!raw.inProgress,
    queueId: raw.queueId == null ? null : raw.queueId,
    stage: raw.stage == null ? null : Number(raw.stage),
    startedBy: person(raw.startedBy),
    startedAt: raw.startedAt || null,
    totalRows: Number(raw.totalRows) || 0,
    processedRows: Number(raw.processedRows) || 0,
    lastResult: lastResult(raw.lastResult),
  };
}

/**
 * getdetails `data` is `{ metadata, tbUpload }`. Older or alternate shapes
 * may put the same object on data[0]. Absent means the caller should keep
 * the current value (the field is landing with the backend change).
 * @returns {object|null}
 */
export function tbUploadFromDetails(data) {
  if (!data) return null;
  if (data.tbUpload) return normalizeTbUpload(data.tbUpload);
  if (Array.isArray(data) && data[0] && data[0].tbUpload) return normalizeTbUpload(data[0].tbUpload);
  return null;
}

/** 202 body, or a synthesized queued status when tbUpload is missing. */
export function tbUploadFromStart(payload, rowCount) {
  const source = payload && typeof payload === 'object' ? payload : {};
  if (source.tbUpload) return normalizeTbUpload(source.tbUpload);
  return normalizeTbUpload({
    inProgress: true,
    queueId: source.queueId || null,
    stage: TB_UPLOAD_STAGE.QUEUED,
    startedBy: null,
    startedAt: null,
    totalRows: Number(rowCount) || 0,
    processedRows: 0,
    lastResult: null,
  });
}

function sameQueue(left, right) {
  return left != null && right != null && String(left) === String(right);
}

/** True only when both timestamps parse and later is strictly after earlier. */
function isStrictlyAfter(later, earlier) {
  if (later == null || later === '' || earlier == null || earlier === '') return false;
  const laterMs = new Date(later).getTime();
  const earlierMs = new Date(earlier).getTime();
  if (!Number.isFinite(laterMs) || !Number.isFinite(earlierMs)) return false;
  return laterMs > earlierMs;
}

function isFinishedStage(stage) {
  return stage === TB_UPLOAD_STAGE.FINISHED || stage === TB_UPLOAD_STAGE.FAILED;
}

/**
 * getdetails and a status poll can be stale next to a job this tab already accepted.
 * An idle payload replaces the running job when it is that job's result, or when
 * lastResult finished (stage 3 or 4) strictly after this job's startedAt.
 * A missing timestamp, or a finishedAt that is not after startedAt, keeps the
 * reject: notification may omit finishedAt until its schema fix ships, and a
 * stale pre-upload getdetails must not clear a job that just started.
 */
export function mergeTbUpload(current, incoming) {
  const base = current && typeof current === 'object' ? current : emptyTbUpload();
  if (!incoming) return base;
  if (base.inProgress && !incoming.inProgress) {
    const result = incoming.lastResult;
    const finishedId = result && result.queueId;
    const sameJob = sameQueue(base.queueId, finishedId);
    const finishedStage = result && isFinishedStage(result.stage);
    const unmatchedFinish = finishedStage && (!base.queueId || finishedId == null);
    const newerFinish = finishedStage && isStrictlyAfter(result && result.finishedAt, base.startedAt);
    if (!sameJob && !unmatchedFinish && !newerFinish) return base;
    return incoming;
  }
  if (!base.inProgress && incoming.inProgress) {
    const finishedId = base.lastResult && base.lastResult.queueId;
    if (sameQueue(finishedId, incoming.queueId)) return base;
  }
  return incoming;
}

export function tbUploadProgressLabel(tbUpload) {
  const name = (tbUpload && tbUpload.startedBy && tbUpload.startedBy.name) || TB_UPLOAD_MESSAGES.UNKNOWN_STARTER;
  const processed = Number(tbUpload && tbUpload.processedRows) || 0;
  const total = Number(tbUpload && tbUpload.totalRows) || 0;
  return `${TB_UPLOAD_MESSAGES.IN_PROGRESS_PREFIX} ${name} — ${processed}/${total} rows`;
}

/** 0 when the total is unknown so the bar can stay indeterminate. */
export function tbUploadProgressValue(tbUpload) {
  const total = Number(tbUpload && tbUpload.totalRows) || 0;
  if (total <= 0) return 0;
  const processed = Number(tbUpload && tbUpload.processedRows) || 0;
  return Math.max(0, Math.min(100, Math.round((processed / total) * 100)));
}

export function tbUploadResultMessage(tbUpload) {
  const last = tbUpload && tbUpload.lastResult;
  if (last && (last.stage === TB_UPLOAD_STAGE.FAILED || last.error)) {
    return { error: true, message: last.error || TB_UPLOAD_MESSAGES.FAILED };
  }
  return { error: false, message: TB_UPLOAD_MESSAGES.COMPLETED };
}
